import React, { useEffect, useRef, useState } from 'react';
import { History } from 'history';
import { Form } from 'informed';
import { FieldSet, InputDateTime, InputDropdownCourse, InputDropDownTermYear, InputTypeCheck, InputTypeDropdown, InputTypeText, KeyValue } from '../../../elements';
import Axios, { AxiosError } from 'axios';
import { API_URL } from '../../../../lib/const';
import { toast } from 'react-toastify';
import { match } from 'react-router-dom';
import DeleteTransferProcessModal from '../components/DeleteTransferProcessModal';
import Card from '../../../components/Card';
import InputAsyncInformed from '../../../elements/InputAsyncInformed';
import { fetchDocumentTypes } from '../../../services/options_service';
import { fetchUsers } from '../../../services/user_services';
import { PermissionContext } from '../../../../hoc/withPermission';
import consts from '../consts';
import TransferDocumentTypeChecklist from '../components/TransferDocumentTypeChecklist';
import { mergeDocumentOptions } from '../components/processDocumentSelection';
import { IDocumentType } from '../../common/types';


const TransferProcessFormPage =({ history, match }: { history: History, match: match<{ id: string }> }) => {
  const formApiRef = useRef<any>();
  let previousErrors: any = {};

  const [submitting, setSubmitting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [documentOptions, setDocumentOptions] = useState<IDocumentType[]>([]);
  const id = match.params.id;
  const edit = !!id;

  useEffect(() => {
    fetchDocumentTypes({ available_for_transfer: true })
      .then((documents: IDocumentType[]) => {
        setDocumentOptions(current => mergeDocumentOptions(documents, current));
      });
  }, []);

  useEffect(() => {
    if (!id) return;

    Axios.get(`${API_URL}/transfers/processes/${id}`)
      .then(response => {
        const selectedDocuments = response.data.document_types;
        const allowedUsers = (response.data.allowed_users || []).map((user: any) => ({
          ...user,
          label: user.display_name,
          value: user.id,
        }));

        setDocumentOptions(current => mergeDocumentOptions(current, selectedDocuments));
        formApiRef.current.setValues({
          ...response.data,
          allowed_users: allowedUsers,
          course: response.data.course.id,
          term_year: response.data.term_year.id,
        });
      })
      .catch((error: AxiosError) => {
        if (error.response && error.response.status === 404) {
          history.push('/transferencias/processos');
        }
      })
  }, [id])

  const handleCreateTransferProcess = async (values: any) => {
    setSubmitting(true);

    const payload = {
      ...values,
      // informed 1.x retains the initial onSubmit; read selection from its current values.
      document_types: (values.document_types || []).map((documentType: IDocumentType) => documentType.id),
      allowed_users: values.kind === consts.TransferProcessKind.CONVOCATION
        ? (values.allowed_users || []).map((user: any) => user.id)
        : [],
    }

    try {
      if (edit) {
        await Axios.patch(`${API_URL}/transfers/processes/${id}`, payload);

        toast.success('Processo de transferência editado com sucesso.')

      } else {
        await Axios.post(`${API_URL}/transfers/processes`, payload);

        toast.success('Processo de transferência criado com sucesso.')
        history.push(`/transferencias/processos`)
      }

    } catch (error) {
      const response = (error as AxiosError).response;

      if (response && response.status === 400) {
        const errors = response.data;
        const formApi = formApiRef.current;

        Object.keys(previousErrors).forEach((fieldName) => {
          if (formApi.fieldExists(fieldName)) {
            formApi.setError(fieldName, null);
          }
        });

        previousErrors = { ...errors };

        Object.entries(errors).forEach((e) => {
          if (formApi.fieldExists(e[0])) {
            formApi.setError(e[0], e[1]);
          }
        });

        toast.error(errors.non_field_errors || 'Ocorreu um erro ao criar o processo de transferência.')

      } else {
        toast.error('Ocorreu um erro desconhecido ao criar o processo de transferência.')

      }

    } finally {
      setSubmitting(false);

    }
  }

  const handleDeleteTransferProcess = async () => {
    return Axios.delete(`${API_URL}/transfers/processes/${id}`)
      .then(() => {
        history.push('/transferencias/processos')
        toast.success('Processo de transferência removido com sucesso.')

      })
      .catch(() => {
        toast.success('Ocorreu um erro ao remover o processo de transferência.')

      })
  }

  return (
    <Card title={edit ? `Processo de Transferência #${id}` : 'Novo Processo de Transferência'}>
      <Form
        onSubmit={handleCreateTransferProcess}
        getApi={(api: any) => { formApiRef.current = api }}
      >
        {({ formState }: { formState: { values: any } }) => (
          <>
            <div className="row">
              <InputTypeText field="name" label="Nome" col="12" required />
              <InputDropdownCourse col="6" required />
              <InputDropDownTermYear col="6" required />
              <InputDateTime field="start_date" label="Data de Início" col="6" />
              <InputDateTime field="end_date" label="Date de Termino" col="6" />
              <InputTypeCheck field="active" label="Ativo" col="6" />
              <InputTypeDropdown
                field="kind"
                label="Tipo de inscrição"
                placeholder="Selecione"
                options={[
                  { value: consts.TransferProcessKind.PUBLIC, label: consts.TransferProcessKind.PUBLIC },
                  { value: consts.TransferProcessKind.CONVOCATION, label: consts.TransferProcessKind.CONVOCATION },
                ]}
                col="6"
              />
            </div>
            <FieldSet title="Documentações">
              <TransferDocumentTypeChecklist
                field="document_types"
                options={documentOptions}
              />
            </FieldSet>
            {formState.values.kind === consts.TransferProcessKind.CONVOCATION && (
              <FieldSet title="Usuários com acesso">
                <InputAsyncInformed
                  isMulti
                  defaultOptions
                  className="w-100 mb-2 mt-0"
                  field="allowed_users"
                  placeholder="Todos os usuários autorizados"
                  loadOptions={(search: string) => fetchUsers(
                    search, { is_staff: true }
                  ).then(data => (data.results || []).map((user: any) => ({
                    ...user,
                    label: user.last_name || user.username,
                    value: user.id,
                  })))}
                />
                <small className="d-block mb-3 text-muted">
                  Sem usuários selecionados, todos que já possuem permissão no módulo poderão acessar.
                </small>
              </FieldSet>
            )}
            {edit && (
              <FieldSet title="Processos disponíveis">
                { (formState.values.avaiable_grades || []).map(((o: any) => (
                  <>
                    <KeyValue col={6} title={o.name} label="Processo" />
                    <KeyValue col={6} title={o.field_step} label="Ano/Período de promoção" />
                  </>
                )))}
              </FieldSet>
            )}
            <div className="p-0 mt-3 d-flex justify-content-between">
              <div className="w-100">
                <button
                  className="btn btn-black mr-2 col-md-5"
                  type="button"
                  onClick={() => history.push('/transferencias/processos')}
                >
                  Voltar
                </button>
              </div>
              <div className="w-100 d-flex justify-content-end">
                <PermissionContext.Consumer>
                  {({ hasPermission }: { hasPermission: (permission: string) => boolean }) => hasPermission('transfers.delete_transferprocess') && edit && (
                    <button
                      className="btn btn-danger mr-2 col-md-5"
                      type="button"
                      onClick={() => setShowDeleteModal(true)}
                    >
                      Deletar
                    </button>
                  )}
                </PermissionContext.Consumer>
                <button
                  disabled={submitting}
                  className="btn btn-primary col-md-5"
                  type="submit"
                >
                  Salvar
                </button>
              </div>
            </div>
          </>
        )}
      </Form>
      <DeleteTransferProcessModal
        show={showDeleteModal}
        onDelete={handleDeleteTransferProcess}
        onClose={() => setShowDeleteModal(false)}
      />
    </Card>
  )
}

export default TransferProcessFormPage;
